import { ApolloError, useLazyQuery, useMutation } from '@apollo/client'
import {
  createClosingComment,
  createComment,
  encryptFileWithReportKey,
  type FileDescriptor,
  loadFileWithReportKey,
  readComment,
  Symmetric,
} from '@faceup/crypto'
import { sharedMessages, useIntl } from '@faceup/localization'
import { type Attachment, generateFileCompareKey } from '@faceup/ui'
import { useNotification } from '@faceup/ui-base'
import { compact, FormItemType, NUMBER_FOR_NO_LIMIT } from '@faceup/utils'
import { captureException } from '@sentry/react'
import { useState } from 'react'
import { graphql } from '../__generated__'
import type { RedactedTextsWithCaseId, SubText } from '../components/Redaction/redactionConstants'
import { isTextFullyAnonymized, useCryptoErrorHandler } from '../utils'
import { isAttachmentDropped } from './redactedAttachmentFilter'
import { useClosingComment } from './useClosingComment'
import { parseGqlAnswerToAnswersType } from './useManageReportCustomInputs'
import { usePrepareFormItemAnswersForMutation } from './usePrepareFormItemAnswersForMutation'
import { usePrepareReportPayload } from './usePrepareReportPayload'
import { useReportBody } from './useReportBody'

const query = {
  UseCreateRedactedReportQuery: graphql(`
    query UseCreateRedactedReportQuery(
      $caseId: UUID!
      $sourceLanguage: Language
      $targetLanguage: Language
      $followUpFirst: Int!
      $commentsRowsPerPage: Int!
      $isVictim: Boolean!
    ) {
      case(caseId: $caseId) {
        id
        ... on CompanyReport {
          category {
            id
          }
        }
        encryptionKey
        organizationalUnit {
          id
          isE2EE
          recipients {
            ...usePrepareReportPayloadFragments_member
          }
        }
        channel {
          id
          defaultLanguage
        }
        closingCommentTranslation(
          sourceLanguage: $sourceLanguage
          targetLanguage: $targetLanguage
        ) {
          id
          body
          bodyNonce
        }
        answers {
          id

          formItem {
            id
            formItemId
            type
          }
          attachments {
            id
            mimetype
            name
            url
          }
          ...usePrepareFormItemAnswersForMutationFragments_answer
          ...useManageReportCustomInputsFragments_answer
        }
        followUpActivities(first: $followUpFirst) {
          items {
            id
            translation(sourceLanguage: $sourceLanguage, targetLanguage: $targetLanguage) {
              id
              ciphertext
              nonce
            }
            attachments {
              id
              name
              url
              mimetype
            }
            # TODO: Should be refactored into separate hook?
            ...FollowUpChatMessage_followUpActivityNode
          }
        }
        comments(page: 0, rowsPerPage: $commentsRowsPerPage) {
          totalCount
          items {
            id
            translation {
              id
              ciphertext
              nonce
            }
            parentComment {
              id
            }
            attachments {
              id
              name
              url
              mimetype
            }
          }
        }
        ...UseReportBody_reportNoVariables
      }
      systemInfo {
        id
        publicKey

        ...usePrepareReportPayloadFragments_system
      }
      memberViewer {
        id
        keys {
          id
          publicKey
        }
      }
    }
  `),
}

const mutations = {
  CreateRedactedReport: graphql(`
    mutation CreateRedactedReport($input: CreateRedactedReportInput!) {
      createRedactedReport(input: $input) {
        createdCase {
          id
          tag
        }
      }
    }
  `),
}

type UseCreateRedactedReport = (
  motherId: string,
  reportId: string
) => {
  createRedactedReport: (input: RedactedTextsWithCaseId) => Promise<string | null>
  loading: boolean
}

export const useCreateRedactedReport: UseCreateRedactedReport = () => {
  const { formatMessage } = useIntl()
  const { decrypt } = useReportBody()
  const { decrypt: decryptClosingComment } = useClosingComment()
  const prepareReportPayload = usePrepareReportPayload()
  const cryptoErrorHandler = useCryptoErrorHandler()
  const { prepareForm } = usePrepareFormItemAnswersForMutation()
  const notification = useNotification()
  const [loading, setLoading] = useState<boolean>(false)
  const [getReportData] = useLazyQuery(query.UseCreateRedactedReportQuery, {
    variables: {
      caseId: '',
      sourceLanguage: null,
      targetLanguage: null,
      followUpFirst: NUMBER_FOR_NO_LIMIT,
      commentsRowsPerPage: NUMBER_FOR_NO_LIMIT,
      isVictim: false,
    },
  })

  const [create] = useMutation(mutations.CreateRedactedReport)

  const createRedactedReport = async (input: RedactedTextsWithCaseId) => {
    try {
      return await buildAndCreateRedactedReport(input)
    } catch (error) {
      // An API error is already shown by the error link and reported by the backend, and a
      // decryption failure below is captured with its context before it is rethrown
      if (!(error instanceof ApolloError) && !(error instanceof ReportedDecryptionError)) {
        captureException(error, {
          tags: { type: 'report_encryption' },
          extra: { flow: 'create-redacted-report', caseId: input.caseId },
        })
      }
      return null
    } finally {
      setLoading(false)
    }
  }

  const buildAndCreateRedactedReport = async (input: RedactedTextsWithCaseId) => {
    setLoading(true)
    const { data } = await getReportData({
      variables: {
        caseId: input.caseId,
        followUpFirst: NUMBER_FOR_NO_LIMIT,
        commentsRowsPerPage: NUMBER_FOR_NO_LIMIT,
        isVictim: false,
      },
    })
    const aCase = data?.case
    const report = data?.case?.__typename === 'CompanyReport' ? data?.case : null

    if (!aCase) {
      setLoading(false)
      return null
    }
    const decryptedData = report ? await decrypt({ reportNoVar: report }) : null
    const organizationalUnit = aCase.organizationalUnit
    const system = data?.systemInfo

    if (!system) {
      setLoading(false)
      return null
    }

    const loadAttachments = async (
      attachments: (FileDescriptor & { id: string })[]
    ): Promise<Attachment[]> =>
      await Promise.all(
        attachments
          ?.filter(attachment => !isTextFullyAnonymized(input.attachments?.[attachment.id] ?? ''))
          .map(async attachment => {
            const file = await loadFileWithReportKey(attachment, aCase.encryptionKey ?? '')
            if (file.isErr()) {
              captureException(file.error, {
                tags: { type: 'file_decryption' },
                extra: {
                  flow: 'create-redacted-report-attachment',
                  caseId: aCase.id,
                  attachmentId: attachment.id,
                  fileName: attachment.name,
                  mimetype: attachment.mimetype,
                },
              })
              throw new ReportedDecryptionError(file.error.message)
            }

            return {
              file: file.value,
              compareKey: generateFileCompareKey(file.value),
              loading: false,
              processed: true,
            }
          })
      )

    const loadCustomFieldAttachments = async (): Promise<Record<string, Attachment[]>> => {
      const attachmentsByFormItem = await Promise.all(
        aCase.answers
          .filter(answer => answer.attachments && answer.attachments.length > 0)
          .map(async answer => {
            const files: Attachment[] = await Promise.all(
              answer.attachments
                .filter(
                  attachment =>
                    !isAttachmentDropped(input, answer.formItem.formItemId, attachment.id)
                )
                .map(async attachment => {
                  const file = await loadFileWithReportKey(attachment, aCase.encryptionKey ?? '')
                  if (file.isErr()) {
                    captureException(file.error, {
                      tags: { type: 'file_decryption' },
                      extra: {
                        flow: 'create-redacted-report-custom-field-attachment',
                        caseId: aCase.id,
                        formItemId: answer.formItem.formItemId,
                        attachmentId: attachment.id,
                        fileName: attachment.name,
                        mimetype: attachment.mimetype,
                      },
                    })
                    throw new ReportedDecryptionError(file.error.message)
                  }
                  return {
                    file: file.value,
                    compareKey: generateFileCompareKey(file.value),
                    loading: false,
                    processed: true,
                  }
                })
            )

            return {
              formItemId: answer.formItem.formItemId,
              files,
            }
          })
      )
      return attachmentsByFormItem.reduce<Record<string, Attachment[]>>(
        (acc, { formItemId, files }) => ({
          ...acc,
          [formItemId]: files,
        }),
        {}
      )
    }
    const customFieldsAttachmentsByFieldItem = await loadCustomFieldAttachments()

    const moreInfoFormItem = aCase.answers.find(
      answer => answer.formItem.type === FormItemType.MoreInformation
    )
    const moreInfoAttachments = moreInfoFormItem
      ? customFieldsAttachmentsByFieldItem[moreInfoFormItem.formItem.formItemId]
      : undefined

    const reportKey = await Symmetric.generateKey()

    const originalAnswers = await prepareForm(
      aCase.channel.id,
      report?.category.id ?? null
    ).getValuesForMutation(parseGqlAnswerToAnswersType(aCase.answers), reportKey)

    // A File item the form no longer offers (deleted, or taken off the form) has no answer on the
    // copy, so its files go with the case-level attachments, which the backend puts on the copy's
    // own Attachments answer.
    const attachments = aCase.answers
      .filter(
        ({ formItem }) =>
          formItem.type === FormItemType.File &&
          originalAnswers.every(({ formItemId }) => formItemId !== formItem.formItemId)
      )
      .flatMap(({ formItem }) => customFieldsAttachmentsByFieldItem[formItem.formItemId] ?? [])

    const payload = await prepareReportPayload(
      organizationalUnit.recipients,
      system,
      null,
      organizationalUnit.isE2EE,
      {
        victimName: input.sender ?? decryptedData?.senderName ?? null,
        moreInfo: input.moreInfo ?? decryptedData?.moreInfo ?? '',
        moreInfoAttachments,
        attachments,
      },
      reportKey
    )

    if (payload.isErr()) {
      cryptoErrorHandler(payload.error.message)
      setLoading(false)
      return null
    }

    const { recipientKeys } = payload.value
    const encryptionKey = recipientKeys.find(({ id }) => id === data?.memberViewer?.id)?.key ?? ''

    // Custom file-form-item answers carry decrypted `File`s (loaded above from the
    // original case's key); re-encrypt them with the redacted case's own key before
    // sending, otherwise they'd be stored unencrypted and fail to decrypt afterwards.
    const encryptCustomFieldFiles = async (formItemId: string) =>
      Promise.all(
        (customFieldsAttachmentsByFieldItem[formItemId] ?? []).map(async attachment => {
          const result = await encryptFileWithReportKey(attachment.file, reportKey)
          if (result.isErr()) {
            throw new Error(`Failed to encrypt file: ${result.error.message}`)
          }
          return result.value
        })
      )

    const answers = await Promise.all(
      originalAnswers.map(async answer => {
        const getValue = async (): Promise<{ values: string[] | null; files?: File[] }> => {
          const formItem = input.formItems?.[answer.formItemId]
          if (formItem?.type) {
            switch (formItem.type) {
              case FormItemType.Select:
              case FormItemType.MultiSelect:
              case FormItemType.Scale:
              case FormItemType.Rating:
                if (isTextFullyAnonymized(formItem.value ?? '')) {
                  return { values: null }
                }
                // We need to return original value (because we pass value to input, not formItemId)
                return { values: answer.values }
              case FormItemType.File: {
                if (!formItem.value) {
                  return { values: answer.values }
                }
                return {
                  values: [],
                  files: await encryptCustomFieldFiles(answer.formItemId),
                }
              }
              case FormItemType.MoreInformation:
                // Unreachable in practice — MoreInformation is filtered out of
                // originalAnswers by omitHardcodedFormItem in
                // usePrepareFormItemAnswersForMutation.ts. Branch exists only for
                // type completeness; voice attachments go through moreInfoAttachments.
                return { values: answer.values }
              default:
                // Text fields can carry voice recordings, and touching the field in redaction
                // mode must not take them down with it - the drops are tracked per attachment.
                return {
                  values: formItem ? [formItem.value] : answer.values,
                  files: await encryptCustomFieldFiles(answer.formItemId),
                }
            }
          }
          return {
            values: answer.values,
            files: await encryptCustomFieldFiles(answer.formItemId),
          }
        }
        return {
          ...answer,
          ...(await getValue()),
        }
      })
    )

    const followUpDecryptedMessages: { id: string; text: string; attachments?: Attachment[] }[] =
      await Promise.all(
        compact(aCase.followUpActivities?.items ?? []).map(async message => {
          if (input.chatMessages?.[message?.id ?? ''] !== undefined) {
            return {
              id: message?.id ?? '',
              text: input.chatMessages?.[message?.id ?? ''] ?? '',
              attachments: message?.attachments
                ? await loadAttachments(message.attachments)
                : undefined,
            }
          }

          const payloadComment = await readComment(
            message?.translation?.ciphertext ?? '',
            message?.translation?.nonce ?? '',
            aCase.encryptionKey ?? ''
          )

          if (payloadComment.isErr()) {
            console.error(payloadComment)
            notification.error({
              message: formatMessage(sharedMessages.encryptionError),
              description: payloadComment.error.message,
            })
            throw new Error(payloadComment.error.message)
          }

          return {
            id: message?.id ?? '',
            text: payloadComment.value,
            attachments: message?.attachments
              ? await loadAttachments(message.attachments)
              : undefined,
          }
        })
      )

    const followUpMessages = await Promise.all(
      followUpDecryptedMessages.map(async message => {
        const payload = await createComment(
          message.text,
          encryptionKey,
          message.attachments?.map(attachment => attachment.file)
        )
        if (payload.isErr()) {
          console.error(payload)
          notification.error({
            message: formatMessage(sharedMessages.encryptionError),
            description: payload.error.message,
          })
          throw new Error(payload.error.message)
        }
        return {
          ...payload.value,
          originalCommentId: message.id,
        }
      })
    )

    const decryptedInternalComments = await Promise.all(
      compact(aCase.comments?.items ?? []).map(async message => {
        if (input.internalCommentMessages?.[message?.id ?? ''] !== undefined) {
          return {
            id: message?.id ?? '',
            text: input.internalCommentMessages?.[message?.id ?? ''] ?? '',
            attachments: message?.attachments
              ? await loadAttachments(message.attachments)
              : undefined,
          }
        }

        const payload = await readComment(
          message?.translation?.ciphertext ?? '',
          message?.translation?.nonce ?? '',
          aCase?.encryptionKey ?? ''
        )
        if (payload.isErr()) {
          console.error(payload)
          notification.error({
            message: formatMessage(sharedMessages.encryptionError),
            description: payload.error.message,
          })

          throw new Error(payload.error.message)
        }
        return {
          ...payload,
          id: message?.id ?? '',
          attachments: message?.attachments
            ? await loadAttachments(message.attachments)
            : undefined,
        }
      })
    )

    const internalComments = await Promise.all(
      decryptedInternalComments
        .filter(message => message.text !== undefined)
        .map(async message => {
          const payload = await createComment(
            message.text,
            encryptionKey,
            message.attachments?.map(attachment => attachment.file)
          )
          if (payload.isErr()) {
            console.error(payload)
            notification.error({
              message: formatMessage(sharedMessages.encryptionError),
              description: payload.error.message,
            })
            throw new Error(payload.error.message)
          }
          return {
            ...payload.value,
            originalCommentId: message.id,
          }
        })
    )

    const closingCommentPayload = input.closingComment
      ? await createClosingComment(input.closingComment, encryptionKey)
      : await createClosingComment(
          await decryptClosingComment(
            aCase.closingCommentTranslation?.body ?? null,
            aCase.closingCommentTranslation?.bodyNonce ?? null,
            aCase.encryptionKey
          ),
          encryptionKey
        )

    if (closingCommentPayload.isErr()) {
      console.error(closingCommentPayload?.error.message)
      notification.error({
        message: formatMessage(sharedMessages.encryptionError),
        description: closingCommentPayload?.error.message,
      })
      throw new Error(closingCommentPayload?.error.message)
    }

    const { body: closingCommentBody, nonce: closingCommentNonce } = closingCommentPayload.value

    const redactedChatUsers = getRedactedUsers(input.chatMemberUsername)
    const redactedCommentUserIds = getRedactedUsers(input.internalCommentUsername)
    const { data: createdReportData } = await create({
      variables: {
        input: {
          ...payload.value,
          originalCaseId: input.caseId,
          answers,
          followUpMessages,
          internalComments,
          redactedChatUsers,
          redactedCommentUserIds,
          closingReasonId: undefined,
          reportClosingComment: {
            newClosingComment: {
              body: closingCommentBody,
              nonce: closingCommentNonce,
            },
          },
        },
      },
    })

    setLoading(false)
    return createdReportData?.createRedactedReport?.createdCase?.id ?? null
  }

  return {
    loading,
    createRedactedReport,
  }
}

class ReportedDecryptionError extends Error {}

const getRedactedUsers: (usernames: SubText | undefined) => { id: string }[] = usernames =>
  compact(
    usernames
      ? Object.entries(usernames).map(([id, username]) => {
          if (username === undefined) {
            return null
          }
          return { id }
        })
      : []
  )
